import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Rocket,
  Compass,
  Heart,
  Users,
  Zap,
  Globe2,
  Target,
  Sparkles,
} from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import SiteFooter from '../components/SiteFooter';

const VALUES = [
  {
    icon: Rocket,
    title: 'Ship fast',
    desc: 'Every minute a dreamer spends waiting is a minute their idea gets colder. Speed is a feature.',
    accent: '#00F0FF',
  },
  {
    icon: Heart,
    title: 'Beautiful by default',
    desc: 'AI-generated does not mean AI-ugly. Every output we ship should feel handmade and cared for.',
    accent: '#FF007F',
  },
  {
    icon: Users,
    title: 'Builder-first',
    desc: 'We build for solo makers, agencies, and dreamers. Not enterprises. Not committees.',
    accent: '#A855F7',
  },
  {
    icon: Globe2,
    title: 'Global from day one',
    desc: 'A tool for anyone with an idea and an internet connection. Priced fair. Localized soon.',
    accent: '#22D3EE',
  },
];

const ROADMAP = [
  {
    when: 'Q1 2026',
    status: 'shipped',
    items: [
      'Landing hero with 3D globe',
      '6 free lead-gen tools (Flight, Holiday, Invoice, QR, Resume, PDF)',
      'AI Builder chat with mock preview',
      'Stripe checkout',
      'Google Sign-In',
      'Admin lead console',
    ],
  },
  {
    when: 'Q2 2026',
    status: 'in-progress',
    items: [
      'Themed multi-section preview mockups',
      'User dashboard with credits + history',
      'Newsletter automation',
      'Referral program',
      'Multi-language site (Spanish, French, Hindi first)',
    ],
  },
  {
    when: 'Q3 2026',
    status: 'planned',
    items: [
      'Real code deployment via v0 / WebContainers partnership',
      'API access for Pro/Business',
      'Team seats & shared workspaces',
      'Image generation baked into builder',
      'Voice input via Whisper',
    ],
  },
  {
    when: 'Q4 2026',
    status: 'planned',
    items: [
      'Marketplace of user-shared prompts + templates',
      'Mobile app (iOS + Android)',
      'Custom domain deploys',
      'Enterprise SSO / SAML',
    ],
  },
];

const STATS = [
  { n: '3', label: 'AI models · Claude · GPT-5 · Gemini' },
  { n: '6', label: 'Free tools available today' },
  { n: '4', label: 'Plans, from free to Business' },
  { n: '24h', label: 'Support reply time' },
];

export default function AboutPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans relative overflow-hidden">
      <div
        aria-hidden
        className="absolute -top-40 -left-40 w-[720px] h-[720px] rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00F0FF 0%, transparent 70%)' }}
      />
      <div
        aria-hidden
        className="absolute top-1/2 -right-40 w-[720px] h-[720px] rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #FF007F 0%, transparent 70%)' }}
      />

      <header className="relative z-10 border-b border-white/[0.06] bg-black/40 backdrop-blur-xl">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              data-testid="about-back-btn"
              className="text-white/60 hover:text-white transition"
            >
              <ArrowLeft size={18} />
            </button>
            <BrandLogo size={30} />
          </div>
          <button
            onClick={() => navigate('/build')}
            data-testid="about-nav-build"
            className="rounded-full px-4 py-1.5 text-sm font-semibold text-black"
            style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}
          >
            Launch builder
          </button>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-5xl px-6 py-16">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] font-mono uppercase tracking-[0.25em] text-white/60 mb-6">
            <Compass size={11} /> Our story
          </div>
          <h1 className="text-5xl md:text-7xl font-display font-black tracking-tight leading-[0.95]">
            Building the launchpad
            <br />
            <span
              style={{
                background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              for dreamers.
            </span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-white/70 leading-relaxed">
            Around the World AI is an AI-first platform for anyone who&rsquo;s ever thought
            <span className="text-white font-semibold"> &ldquo;I wish I could just build that&rdquo;</span> —
            without spending months learning to code or thousands hiring a team.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
          {STATS.map((s, i) => (
            <div
              key={i}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-center"
              data-testid={`about-stat-${i}`}
            >
              <div
                className="text-3xl md:text-4xl font-display font-black"
                style={{
                  background: 'linear-gradient(135deg, #00F0FF 0%, #FF007F 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {s.n}
              </div>
              <div className="text-[11.5px] text-white/55 mt-1.5">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Mission */}
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-8 md:p-12 mb-20">
          <div className="flex items-start gap-4">
            <div
              className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: 'rgba(0,240,255,0.1)', color: '#00F0FF' }}
            >
              <Target size={20} />
            </div>
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/40 mb-2">
                Our mission
              </div>
              <h2 className="text-2xl md:text-3xl font-display font-black tracking-tight leading-tight">
                Democratize the launch layer of the internet.
              </h2>
              <p className="mt-4 text-[15px] text-white/70 leading-relaxed">
                For most of the internet&rsquo;s existence, launching anything real — a website,
                an app, a startup — required a rare mix of coding, design, business, and legal
                skill. We&rsquo;re building the AI-first shortcut. Whether you&rsquo;re a chef who
                wants a delivery site, a coach who wants a booking app, or a student with a
                billion-dollar idea, our goal is to get you from &ldquo;prompt&rdquo; to
                &ldquo;live&rdquo; in under an hour.
              </p>
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/40 mb-2">
              What we believe
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-black tracking-tight">
              Four principles that guide every pixel.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  data-testid={`about-value-${v.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.04] transition"
                >
                  <div
                    className="h-10 w-10 rounded-xl flex items-center justify-center"
                    style={{ background: `${v.accent}22`, color: v.accent }}
                  >
                    <Icon size={18} />
                  </div>
                  <h3 className="mt-4 text-lg font-display font-bold text-white">{v.title}</h3>
                  <p className="mt-2 text-[14px] text-white/65 leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Roadmap */}
        <div id="roadmap" className="mb-20">
          <div className="text-center mb-10">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/40 mb-2">
              What we&rsquo;re building
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-black tracking-tight">
              Public roadmap.
            </h2>
            <p className="mt-3 text-white/55">
              We build in the open. Here&rsquo;s where we&rsquo;re headed.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-4" data-testid="about-roadmap">
            {ROADMAP.map((q) => {
              const badge =
                q.status === 'shipped'
                  ? { color: '#10B981', text: 'Shipped' }
                  : q.status === 'in-progress'
                    ? { color: '#F59E0B', text: 'In progress' }
                    : { color: '#94A3B8', text: 'Planned' };
              return (
                <div
                  key={q.when}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[13px] font-mono font-bold text-white">{q.when}</div>
                    <div
                      className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full"
                      style={{
                        background: `${badge.color}18`,
                        color: badge.color,
                        border: `1px solid ${badge.color}30`,
                      }}
                    >
                      {badge.text}
                    </div>
                  </div>
                  <ul className="space-y-1.5">
                    {q.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-[13px] text-white/70">
                        <Sparkles size={11} className="text-white/40 mt-1 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div
          className="rounded-3xl p-10 md:p-14 text-center relative overflow-hidden"
          style={{
            background:
              'radial-gradient(600px 300px at 0% 0%, rgba(0,240,255,0.15) 0%, transparent 60%),' +
              'radial-gradient(600px 300px at 100% 100%, rgba(255,0,127,0.15) 0%, transparent 60%),' +
              'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <Zap size={28} className="mx-auto text-white/80" />
          <h3 className="mt-5 text-3xl md:text-4xl font-display font-black tracking-tight">
            Ready to launch what&rsquo;s in your head?
          </h3>
          <p className="mt-3 text-white/60 max-w-xl mx-auto">
            Start free, no credit card. Take five minutes. Surprise yourself.
          </p>
          <div className="mt-7 flex items-center justify-center gap-3 flex-wrap">
            <button
              onClick={() => navigate('/build')}
              data-testid="about-cta-build"
              className="rounded-full px-6 py-3 text-sm font-semibold text-black"
              style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}
            >
              Launch the builder →
            </button>
            <button
              onClick={() => navigate('/pricing')}
              data-testid="about-cta-pricing"
              className="rounded-full border border-white/15 hover:bg-white/[0.05] px-6 py-3 text-sm text-white transition"
            >
              See pricing
            </button>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
